import React, { useEffect, useState } from 'react';
import { HashRouter, Routes, Route, Link, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Button, Container } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import AssignmentManager from './components/AssignmentManager';
import PeopleManager from './components/PeopleManager';
import TaskManager from './components/TaskManager';
import ReportsManager from './components/ReportsManager';
import SpecialsManager from './components/SpecialsManager';
import Landing from './components/Landing';
import config from './config';
import axios from 'axios';

const AppContent = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check authentication status only on initial load
    axios.get(`${config.api.baseUrl}/me`, { withCredentials: true })
      .then(() => {
        setIsAuthenticated(true);
        // Only redirect to assignments if we're on the landing page
        if (location.pathname === '/') {
          navigate('/assignments');
        }
      })
      .catch(() => {
        setIsAuthenticated(false);
        // If not authenticated and not on landing, redirect to landing
        if (location.pathname !== '/') {
          navigate('/');
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []); // Only run on mount

  const handleLogout = async () => {
    try {
      await axios.post(`${config.api.baseUrl}/auth/logout`, {}, { withCredentials: true });
      setIsAuthenticated(false);
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      // Even if the server call fails, try to logout locally
      setIsAuthenticated(false);
      navigate('/');
    }
  };

  if (isLoading) {
    return null; // or a loading spinner
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {isAuthenticated && (
        <AppBar position="static" className="print-hide-header">
          <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 48, sm: 64 } }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                minWidth: 0,
                overflowX: 'auto',
                overflowY: 'hidden',
                WebkitOverflowScrolling: 'touch',
                scrollbarWidth: 'thin',
                py: 0.5,
              }}
            >
              <Button color="inherit" component={Link} to="/assignments" sx={{ flexShrink: 0 }}>
                Assignments
              </Button>
              <Button color="inherit" component={Link} to="/people" sx={{ flexShrink: 0 }}>
                Murids
              </Button>
              <Button color="inherit" component={Link} to="/tasks" sx={{ flexShrink: 0 }}>
                Varos
              </Button>
              <Button color="inherit" component={Link} to="/reports" sx={{ flexShrink: 0 }}>
                Reports
              </Button>
              <Button color="inherit" component={Link} to="/specials" sx={{ flexShrink: 0 }}>
                Special
              </Button>
              <Button
                color="inherit"
                onClick={handleLogout}
                sx={{ flexShrink: 0, display: { xs: 'inline-flex', sm: 'none' } }}
              >
                Logout
              </Button>
            </Box>
            <Button
              color="inherit"
              onClick={handleLogout}
              sx={{ flexShrink: 0, display: { xs: 'none', sm: 'inline-flex' }, ml: 1 }}
            >
              Logout
            </Button>
          </Toolbar>
        </AppBar>
      )}
      <Container sx={{ mt: 4 }}>
        <Routes>
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Navigate to="/assignments" replace />
              ) : (
                <Landing />
              )
            }
          />
          <Route
            path="/assignments"
            element={
              isAuthenticated ? (
                <AssignmentManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/people"
            element={
              isAuthenticated ? (
                <PeopleManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/tasks"
            element={
              isAuthenticated ? (
                <TaskManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/reports"
            element={
              isAuthenticated ? (
                <ReportsManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="/specials"
            element={
              isAuthenticated ? (
                <SpecialsManager />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </Container>
    </Box>
  );
};

const App = () => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <HashRouter>
        <AppContent />
      </HashRouter>
    </LocalizationProvider>
  );
};

export default App;
